import React, { useState } from "react";
import { FileText, FileSpreadsheet, File as FileIcon, Download } from "lucide-react";
import { API } from "../../lib/api";
import { fmtSize } from "./AttachmentPicker";

function truncate(s, n = 40) {
    if (!s) return "Allegato senza nome";
    return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function iconFor(mime) {
    if (!mime) return <FileIcon size={16} strokeWidth={2} />;
    if (mime === "application/pdf") return <FileText size={16} strokeWidth={2} />;
    if (mime.includes("spreadsheet") || mime === "text/csv" || mime === "application/vnd.ms-excel")
        return <FileSpreadsheet size={16} strokeWidth={2} />;
    if (mime.includes("word") || mime === "application/msword")
        return <FileText size={16} strokeWidth={2} />;
    return <FileIcon size={16} strokeWidth={2} />;
}

function mimeLabel(mime) {
    if (!mime) return "FILE";
    if (mime === "application/pdf") return "PDF";
    if (mime.includes("spreadsheet") || mime === "application/vnd.ms-excel") return "XLS";
    if (mime === "text/csv") return "CSV";
    if (mime.includes("word") || mime === "application/msword") return "DOC";
    if (mime.startsWith("image/")) return mime.split("/")[1].toUpperCase();
    return "FILE";
}

function fullUrl(pathOrUrl) {
    if (!pathOrUrl) return "#";
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    // download_url dal backend è path assoluto tipo "/api/internal/attachments/{id}"
    const base = (API || "").replace(/\/api\/?$/, "");
    return base + pathOrUrl;
}

function ImageThumb({ att }) {
    const [failed, setFailed] = useState(false);
    const src = fullUrl(att.download_url);
    if (failed) {
        return (
            <div className="lt-attach-card lt-attach-card--broken" data-testid={`att-broken-${att.id}`}>
                <span className="lt-attach-card__icon">{iconFor(att.content_type)}</span>
                <div className="lt-attach-card__meta">
                    <div className="lt-attach-card__name">Immagine non disponibile</div>
                    <div className="lt-attach-card__sub">{truncate(att.original_name)}</div>
                </div>
            </div>
        );
    }
    return (
        <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="lt-attach-thumb"
            title={att.original_name || "Immagine"}
            data-testid={`att-thumb-${att.id}`}
        >
            <img
                src={src}
                alt={att.original_name || "immagine"}
                onError={() => setFailed(true)}
                loading="lazy"
            />
        </a>
    );
}

function FileCard({ att }) {
    const src = fullUrl(att.download_url);
    return (
        <a
            href={src}
            download={att.original_name || undefined}
            target="_blank"
            rel="noopener noreferrer"
            className="lt-attach-card"
            title={att.original_name || "Allegato"}
            data-testid={`att-card-${att.id}`}
        >
            <span className="lt-attach-card__icon">{iconFor(att.content_type)}</span>
            <div className="lt-attach-card__meta">
                <div className="lt-attach-card__name">{truncate(att.original_name)}</div>
                <div className="lt-attach-card__sub">
                    {mimeLabel(att.content_type)} · {fmtSize(att.size)}
                </div>
            </div>
            <span className="lt-attach-card__action" aria-hidden="true">
                <Download size={14} strokeWidth={2} />
            </span>
        </a>
    );
}

/**
 * Rendering inline degli allegati di un messaggio.
 * - `attachments` può essere undefined/null → non renderizza nulla
 * - Immagini → thumbnail 200x200 con lazy loading, click → nuova tab
 * - PDF/Office/altri → card cliccabile → download
 */
export default function AttachmentBubble({ attachments }) {
    if (!attachments || attachments.length === 0) return null;
    return (
        <div className="lt-attach-bubble" data-testid="attach-bubble">
            {attachments.map((a) =>
                a.is_image
                    ? <ImageThumb key={a.id} att={a} />
                    : <FileCard key={a.id} att={a} />,
            )}
        </div>
    );
}
